import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  CircularProgress,
} from '@mui/material';
import GroupWork from '@mui/icons-material/GroupWork';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../contexts/AuthContext';

const communityRegistrationSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type CommunityRegistrationFormData = z.infer<typeof communityRegistrationSchema>;

interface CommunityRegistrationProps {
  onRegistrationSuccess: () => void;
  onBackToLogin: () => void;
}

export const CommunityRegistration: React.FC<CommunityRegistrationProps> = ({
  onRegistrationSuccess,
  onBackToLogin,
}) => {
  const { registerCommunity } = useAuth();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CommunityRegistrationFormData>({
    resolver: zodResolver(communityRegistrationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: CommunityRegistrationFormData) => {
    setError('');
    setLoading(true);

    try {
      await registerCommunity(
        {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
        },
        data.password
      );

      onRegistrationSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#f3f4f6',
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            overflow: 'hidden',
            borderRadius: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
              py: { xs: 4, sm: 5 },
              px: { xs: 3, sm: 4 },
              textAlign: 'center',
              color: 'white',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                borderRadius: '50%',
                bgcolor: 'white',
                mb: { xs: 2, sm: 3 },
              }}
            >
              <GroupWork sx={{ fontSize: { xs: 36, sm: 48 }, color: '#0f766e' }} />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              fontWeight={700}
              sx={{
                color: 'white',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Community Member Registration
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              Create an account to purchase event tickets and community items
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'white' }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                Account Information
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="First Name"
                        fullWidth
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Last Name"
                        fullWidth
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email Address"
                        type="email"
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number"
                        fullWidth
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Password"
                        type="password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mb: 3 }}>
                As a community member, you'll be able to purchase event tickets and other items available to the public.
              </Alert>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
              </Box>

              <Button
                startIcon={<ArrowBack />}
                onClick={onBackToLogin}
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading}
              >
                Back to Login
              </Button>
            </form>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
